import { isApplicationWebMcpMode } from "@cogo/types-v2"
import { hashCanonicalContent } from "@cogo/schema/canonical-content-hash"
import {
  admitProjectValidationAuthorityEnvelope,
  hashProjectValidationAuthorityValue,
} from "../composition/admit-application-composition"
import { compileDesignBoundApplication } from "../design-ontology-binding/compile-design-bound-application"
import {
  DESIGN_BINDING_SHARD_PROJECTION,
  createShardBackedDesignOntologyReleaseResolver,
  findDesignBindingShardRecord,
} from "../design-ontology-binding/shard-backed-release-resolver"
import { stableJsonSha256 } from "../design-ontology-binding/stable-json-hash"
import {
  resolveApplicationProfileLibraryContract,
  type ApplicationProfileLibraryContract,
} from "../application-profile-library-contract"
import type { DesignBoundApplicationArtifact } from "../design-ontology-binding/types"
import type {
  LibraryKnowledgeBundleProvenanceEnvelope,
  ProjectValidationLibraryKnowledgeBundleProvenance,
  SourceInteractionRelationSetV1,
} from "../library-knowledge-bundle/types"
import {
  hashPlatformCanonicalJson,
  parsePlatformLibraryRagCriticReceiptV1,
  parsePlatformLibraryRagPlanReceiptV1,
  type PlatformLibraryRagCriticReceiptV1,
  type PlatformLibraryRagPlanReceiptV1,
} from "../library-knowledge-bundle/platform-library-rag-contract"
import type {
  ApplicationBlueprint,
  ApplicationCapabilityRequirement,
  UxPatternDefinition,
} from "../library-composition/types"
import type {
  LibraryAgentIdentity,
  LibraryDesignBindingEvidence,
  LibraryKnowledgeAsset,
  LibraryKnowledgeBundle,
  LibraryKnowledgeSummary,
  LibraryRagOrchestrationDependencies,
  LibraryRagOrchestrationGap,
  LibraryRagOrchestrationResult,
  LibraryRagOrchestrationRequest,
  LibraryRagSelection,
  LibrarySemanticCriticVerdict,
} from "./types"

// Process-local success admission used by the bounded Astryx/Library seam. It
// rejects cloned lookalikes and any post-success mutation locally; it is not a
// durable receipt or a claim of Studio, generation, publication, or deployment
// authority.
const runtimeAdmittedSuccesses = new WeakMap<object, `sha256:${string}`>()

export async function orchestrateLibraryRagApplication(
  request: LibraryRagOrchestrationRequest,
  dependencies: LibraryRagOrchestrationDependencies,
): Promise<LibraryRagOrchestrationResult> {
  const invalidRequest = validateRequest(request)
  if (invalidRequest) return invalidRequest

  // Task 10 — the generic profile-side authority, taken through the ONE
  // closed guard both halves of the generator use. A supplied-but-unadmitted
  // contract is refused here rather than dropped: an ignored authority key is
  // indistinguishable, from the caller's side, from an honoured one.
  let profileLibraryContract: ApplicationProfileLibraryContract | undefined
  if (request.profileLibraryContract !== undefined) {
    const resolution = resolveApplicationProfileLibraryContract(
      request.profileLibraryContract,
    )
    if (!resolution.ok) {
      return failure(
        "retrieval",
        "library-rag-candidates-missing",
        "$.profileLibraryContract",
        "The supplied application profile library contract is not admitted.",
      )
    }
    profileLibraryContract = resolution.contract
  }
  void profileLibraryContract

  let bundle: unknown
  try {
    const retrievedBundle = await dependencies.gateway.retrieve(
      structuredClone(request),
    )
    bundle = structuredClone(retrievedBundle)
  } catch {
    return failure("retrieval", "library-rag-retrieval-failed", "$.retrieve", "Library knowledge retrieval failed.")
  }
  if (!hasCandidates(bundle, request)) {
    return failure("retrieval", "library-rag-candidates-missing", "$.retrieve", "Library knowledge candidates are missing.")
  }
  if (!(await hasMatchingSourceInteractionRelationSets(bundle, request))) {
    return failure(
      "retrieval",
      "library-rag-candidates-missing",
      "$.retrieve.sourceInteractionRelationSets",
      "Library source interaction authority is malformed or does not match the retrieved subject.",
    )
  }
  if (!(await hasMatchingKnowledgeProvenance(bundle, request))) {
    return failure("retrieval", "library-rag-candidates-missing", "$.retrieve.provenance", "Library knowledge provenance does not match the requested authority.")
  }
  let plannerIdentity: LibraryAgentIdentity<"knowledge-planner">
  try {
    plannerIdentity = structuredClone(dependencies.planner.identity)
  } catch {
    return failure("knowledge-planner", "library-rag-agent-role-invalid", "$.planner.identity", "The knowledge planner role is invalid.")
  }
  if (!isAgentIdentity(plannerIdentity, "knowledge-planner")) {
    return failure("knowledge-planner", "library-rag-agent-role-invalid", "$.planner.identity.role", "The knowledge planner role is invalid.")
  }

  let plannerSelection: unknown
  try {
    plannerSelection = await dependencies.planner.select({
      request: structuredClone(request),
      catalog: projectLibraryKnowledgeSummaries(bundle),
    })
  } catch {
    return failure("knowledge-planner", "library-rag-planner-failed", "$.planner", "Library knowledge planning failed.")
  }
  let selection: unknown
  let plannerAuthorityReceipt: PlatformLibraryRagPlanReceiptV1 | undefined
  try {
    if (isAuthorityPlannerOutput(plannerSelection)) {
      plannerAuthorityReceipt =
        await parsePlatformLibraryRagPlanReceiptV1(
          plannerSelection.authorityReceipt,
        )
      selection = structuredClone(plannerSelection.selection)
      if (
        !(await platformPlanBindsRequestAndSelection(
          plannerAuthorityReceipt,
          request,
          selection,
        ))
      ) {
        return failure(
          "knowledge-planner",
          "library-rag-planner-failed",
          "$.planner.authorityReceipt",
          "The platform planning receipt does not bind this request and selection.",
        )
      }
      plannerIdentity = structuredClone(plannerAuthorityReceipt.planner.identity)
    } else {
      selection = structuredClone(plannerSelection)
    }
  } catch {
    return invalidSelection("The Library RAG selection is invalid.").result
  }

  const resolved = resolveSelection(
    bundle,
    selection,
    request.applicationInstanceId,
  )
  if (!resolved.ok) return resolved.result

  const input = {
    library: {
      applicationInstanceId: request.applicationInstanceId,
      applicationBlueprint: structuredClone(resolved.applicationBlueprint),
      uxPatterns: structuredClone(resolved.uxPatterns),
      capabilityRequirement:
        await requirementForSelectedBlueprint(request, resolved.applicationBlueprint),
    },
    libraryAuthority: structuredClone(bundle.authorityContext),
    designOntologyReleaseRef: resolved.selection.designOntologyReleaseRef,
    ...(resolved.sourceInteractionRelationSet !== null
      ? {
          sourceInteractionRelationSet: structuredClone(
            resolved.sourceInteractionRelationSet,
          ),
          sourceInteractionRelations: structuredClone(
            resolved.sourceInteractionRelations,
          ),
        }
      : {}),
    ...(request.projectValidationAuthority
      ? { projectValidationAuthority: request.projectValidationAuthority }
      : {}),
  }
  // The Design Ontology release authority. The DEFAULT is the L1 binding shard,
  // not the retrieved bundle: a bundle's release payload is retrieval output,
  // not a design source of truth, and compiling against it would make the
  // shard-backed resolver exported-but-unreachable. A caller may inject a
  // different, separately admitted authority; see the dependency's doc comment.
  const releaseResolver =
    dependencies.designOntologyReleaseResolver ??
    createShardBackedDesignOntologyReleaseResolver()
  // Capture exactly what the compiler compiled against, so the receipt reports
  // the release the artifact was built from rather than the retrieved copy.
  let compiledAgainstRelease: LibraryKnowledgeBundle["designOntologyReleases"][number]["payload"] | null =
    null
  const compiled = await compileDesignBoundApplication(input, {
    resolveDesignOntologyRelease: async (releaseRef) => {
      const release =
        await releaseResolver.resolveDesignOntologyRelease(releaseRef)
      compiledAgainstRelease = release
      return release
    },
  })
  if (!compiled.ok) return { ok: false, stage: "compiler", gaps: compiled.gaps }
  if (resolved.selection.normalizedIntent !== compiled.artifact.library.runtime.userGoal) {
    return invalidSelection(
      "The normalized intent does not match the compiled application goal.",
    ).result
  }

  let criticIdentity: LibraryAgentIdentity<"semantic-critic">
  try {
    criticIdentity = structuredClone(dependencies.critic.identity)
  } catch {
    return failure("semantic-critic", "library-rag-agent-role-invalid", "$.critic.identity", "The semantic critic role is invalid.")
  }
  if (!isAgentIdentity(criticIdentity, "semantic-critic")) {
    return failure("semantic-critic", "library-rag-agent-role-invalid", "$.critic.identity.role", "The semantic critic role is invalid.")
  }
  const criticSelection = deepFreeze(structuredClone(resolved.selection))
  let verdict: unknown
  let criticAuthorityReceipt: PlatformLibraryRagCriticReceiptV1 | undefined
  try {
    const criticResult = await dependencies.critic.review({
      request: structuredClone(request),
      selection: criticSelection,
      artifact: structuredClone(compiled.artifact),
    })
    if (isAuthorityCriticOutput(criticResult)) {
      criticAuthorityReceipt =
        await parsePlatformLibraryRagCriticReceiptV1(
          criticResult.authorityReceipt,
        )
      verdict = structuredClone(criticResult.verdict)
      if (
        !(await platformCriticBindsCompilation({
          receipt: criticAuthorityReceipt,
          plan: plannerAuthorityReceipt,
          request,
          selection: criticSelection,
          artifact: compiled.artifact,
          verdict: criticResult.verdict,
        }))
      ) {
        return failure(
          "semantic-critic",
          "library-rag-critic-failed",
          "$.critic.authorityReceipt",
          "The platform critic receipt does not bind this plan and compiled artifact.",
        )
      }
      criticIdentity = structuredClone(criticAuthorityReceipt.critic.identity)
    } else {
      verdict = structuredClone(criticResult)
    }
  } catch {
    return failure("semantic-critic", "library-rag-critic-failed", "$.critic", "Library semantic review failed.")
  }
  if (Boolean(plannerAuthorityReceipt) !== Boolean(criticAuthorityReceipt)) {
    return failure(
      "semantic-critic",
      "library-rag-critic-failed",
      "$.critic.authorityReceipt",
      "Platform planning and critic authority receipts must be preserved together.",
    )
  }
  if (!isSemanticCriticVerdict(verdict)) {
    return failure("semantic-critic", "library-rag-critic-failed", "$.critic", "Library semantic review failed.")
  }
  const knownEvidenceRefs = admittedEvidenceRefsFor(compiled.artifact)
  if (!criticEvidenceRefsAreAdmitted(verdict.findings, knownEvidenceRefs)) {
    return failure(
      "semantic-critic",
      "library-rag-critic-failed",
      "$.critic.findings",
      "Library semantic review failed.",
    )
  }
  if (verdict.verdict !== "pass") {
    return failure(
      "semantic-critic",
      "library-rag-semantic-rejected",
      "$.critic.verdict",
      "The semantic critic rejected the composition.",
      exactEvidenceRefs(verdict.findings),
    )
  }

  const success = deepFreeze({
    ok: true,
    input: request.projectValidationAuthority
      ? {
          ...structuredClone(input),
          projectValidationAuthority: request.projectValidationAuthority,
        }
      : structuredClone(input),
    designOntologyRelease: structuredClone(
      compiledAgainstRelease ?? resolved.designOntologyRelease,
    ),
    artifact: structuredClone(compiled.artifact),
    evidence: {
      schemaVersion: "cogo.library-rag-orchestration-evidence.v1",
      retrievalTraceId: bundle.retrievalTraceId,
      request: {
        target: request.target,
        locale: request.locale,
        applicationInstanceId: request.applicationInstanceId,
      },
      planner: {
        identity: structuredClone(plannerIdentity),
        traceId: resolved.selection.traceId,
        ...(plannerAuthorityReceipt
          ? { authorityReceipt: plannerAuthorityReceipt }
          : {}),
      },
      selection: structuredClone(resolved.selection),
      knowledgeProvenance: bundle.provenance
        ? structuredClone(bundle.provenance)
        : null,
      designBinding: await designBindingEvidenceFor(compiled.artifact),
      compiler: {
        designOntologyReleaseRef: compiled.artifact.designOntologyReleaseRef,
        applicationSemanticHash: compiled.artifact.applicationSemanticHash,
        designBindingHash: compiled.artifact.designBindingHash,
        renderContractHash: compiled.artifact.renderContractHash,
      },
      critic: {
        identity: structuredClone(criticIdentity),
        verdict: "pass",
        findings: structuredClone(verdict.findings),
        traceId: verdict.traceId,
        ...(criticAuthorityReceipt
          ? { authorityReceipt: criticAuthorityReceipt }
          : {}),
      },
    },
  } satisfies Extract<LibraryRagOrchestrationResult, { ok: true }>)
  runtimeAdmittedSuccesses.set(success, hashCanonicalContent(success))
  return success
}

function isAuthorityPlannerOutput(value: unknown): value is {
  selection: LibraryRagSelection
  authorityReceipt: PlatformLibraryRagPlanReceiptV1
} {
  return hasExactKeys(value, ["selection", "authorityReceipt"])
}

function isAuthorityCriticOutput(value: unknown): value is {
  verdict: LibrarySemanticCriticVerdict
  authorityReceipt: PlatformLibraryRagCriticReceiptV1
} {
  return hasExactKeys(value, ["verdict", "authorityReceipt"])
}

async function platformPlanBindsRequestAndSelection(
  receipt: PlatformLibraryRagPlanReceiptV1,
  request: LibraryRagOrchestrationRequest,
  selection: unknown,
): Promise<boolean> {
  try {
    return (
      receipt.request.target === request.target &&
      receipt.request.locale === request.locale &&
      receipt.request.applicationInstanceId === request.applicationInstanceId &&
      receipt.requestHash === (await hashPlatformCanonicalJson({
        prompt: request.prompt,
        target: request.target,
        locale: request.locale,
        applicationInstanceId: request.applicationInstanceId,
      })) &&
      (await hashPlatformCanonicalJson(receipt.selection)) ===
        (await hashPlatformCanonicalJson(selection))
    )
  } catch {
    return false
  }
}

async function platformCriticBindsCompilation(input: {
  receipt: PlatformLibraryRagCriticReceiptV1
  plan: PlatformLibraryRagPlanReceiptV1 | undefined
  request: LibraryRagOrchestrationRequest
  selection: LibraryRagSelection
  artifact: DesignBoundApplicationArtifact
  verdict: unknown
}): Promise<boolean> {
  const { receipt, plan, request, selection, artifact, verdict } = input
  if (!plan) return false
  try {
    const artifactHash = await hashPlatformCanonicalJson(artifact)
    return (
      receipt.request.target === request.target &&
      receipt.request.locale === request.locale &&
      receipt.request.applicationInstanceId === request.applicationInstanceId &&
      receipt.requestHash === plan.requestHash &&
      receipt.scopeHash === plan.scopeHash &&
      receipt.bundleContentHash === plan.bundleContentHash &&
      receipt.planReceiptContentHash === plan.contentHash &&
      receipt.compiledArtifactContentHash === artifactHash &&
      (await hashPlatformCanonicalJson(receipt.selection)) ===
        (await hashPlatformCanonicalJson(selection)) &&
      (await hashPlatformCanonicalJson(receipt.critic.verdict)) ===
        (await hashPlatformCanonicalJson(verdict))
    )
  } catch {
    return false
  }
}

/**
 * The L1 Design Ontology binding record behind a compiled artifact.
 *
 * Returns `null` — never a partial or contradicting record — when the shard
 * declares no binding for this application, or declares one against a DIFFERENT
 * release than the artifact was compiled against (which is what an injected
 * non-Library release authority produces). A `null` here is informative: it
 * says the artifact is not bound to the Library's designated release.
 */
/**
 * The single admitted project-validation contract ref of one authority
 * context. Throws rather than defaulting: a project-validation authority that
 * admitted no contract ref is a compiler blocker, and a generator that
 * invented one would put an unadmitted ref into evidence.
 */
export const PROJECT_VALIDATION_CONTRACT_REF_UNADMITTED =
  "project-validation-contract-ref-unadmitted"

function admittedContractRef(context: {
  approvedProjectValidationContractRefs?: readonly string[]
}): string {
  const [admitted, ...rest] = context.approvedProjectValidationContractRefs ?? []
  if (typeof admitted !== "string" || admitted.length === 0 || rest.length > 0) {
    throw new Error(PROJECT_VALIDATION_CONTRACT_REF_UNADMITTED)
  }
  return admitted
}

async function designBindingEvidenceFor(
  artifact: DesignBoundApplicationArtifact,
): Promise<LibraryDesignBindingEvidence | null> {
  const projectAuthority = artifact.generationTrace.projectValidationAuthority
  if (projectAuthority) {
    const releaseBinding = artifact.design.generationTrace.projectValidationAuthority
    if (!releaseBinding || releaseBinding.contentHash !== projectAuthority.contentHash) {
      return null
    }
    const requiredTokenRefs = [...new Set(
      artifact.design.nodes.flatMap((node) => [
        ...node.tokenRefs,
        ...(node.action ? [node.action.tokenRef] : []),
      ]),
    )].sort()
    const body = {
      evidenceKind: "project-validation" as const,
      aggregationScope: projectAuthority.authorityContext.aggregationScope,
      sliceId: projectAuthority.authorityContext.sliceId,
      // Task 10 — the ADMITTED contract ref, read from the authority context
      // this evidence is about. It used to be a hard-coded customer literal
      // sitting inside a record whose every other member was already read
      // from `projectAuthority`: a value computed correctly and then thrown
      // away. A context that admitted no contract ref is a named compiler
      // blocker, never a synthesized default.
      contractRef: admittedContractRef(projectAuthority.authorityContext),
      contractHash: projectAuthority.authorityContext.contractHash,
      applicationCoreRef: projectAuthority.applicationCoreRef,
      designCompositionReleaseRef: projectAuthority.designCompositionReleaseRef,
      designCompositionReleaseHash: projectAuthority.designCompositionReleaseHash,
      sourceMaterializationReceiptRef: projectAuthority.sourceMaterializationReceiptRef,
      sourceMaterializationReceiptHash: projectAuthority.sourceMaterializationReceiptHash,
      authorityContextHash: projectAuthority.authorityContextHash,
      projectValidationAuthorityOptionHash: projectAuthority.contentHash,
      requiredTokenRefs,
      processLocal: true as const,
      persistedOntologyShard: false as const,
      nonPromotion: true as const,
    }
    return { ...body, contentHash: hashProjectValidationAuthorityValue(body) }
  }
  const record = findDesignBindingShardRecord(
    artifact.design.applicationCoreRef,
  )
  if (!record) return null
  if (record.designOntologyReleaseRef !== artifact.designOntologyReleaseRef) {
    return null
  }
  return {
    shardRef: DESIGN_BINDING_SHARD_PROJECTION.shardRef,
    applicationRef: record.applicationRef,
    designOntologyReleaseRef: record.designOntologyReleaseRef,
    requiredTokenRefs: [...record.requiredTokenRefs],
  }
}

function validateRequest(
  request: LibraryRagOrchestrationRequest,
): LibraryRagOrchestrationResult | null {
  if (!request.prompt?.trim()) {
    return failure("request", "library-rag-prompt-empty", "$.prompt", "A Library RAG prompt is required.")
  }
  if (
    request.target !== "web" ||
    !isSafeIdentifier(request.locale, 64) ||
    !isSafeIdentifier(request.applicationInstanceId, 256)
  ) {
    return failure("request", "library-rag-candidates-missing", "$.request", "The Library RAG request is invalid.")
  }
  if (
    request.projectValidationAuthority !== undefined &&
    !admitProjectValidationAuthorityEnvelope(request.projectValidationAuthority)
  ) {
    return failure("request", "library-rag-candidates-missing", "$.projectValidationAuthority", "The project-validation authority is invalid.")
  }
  return null
}

async function hasMatchingKnowledgeProvenance(
  bundle: LibraryKnowledgeBundle<LibraryKnowledgeBundleProvenanceEnvelope>,
  request: LibraryRagOrchestrationRequest,
): Promise<boolean> {
  const authority = request.projectValidationAuthority
  const provenance = bundle.provenance
  if (!authority) {
    if (isProjectValidationProvenance(provenance)) return false
    // Legacy canonical bundles without interaction authority retain their
    // pre-provenance compatibility. A non-empty relation set is executable
    // authority, so its exact bytes must also be bound by the retrieved
    // bundle's provenance hash before selection can observe it.
    if ((bundle.sourceInteractionRelationSets?.length ?? 0) === 0) return true
    if (
      !isRecord(provenance) ||
      provenance.schemaVersion !==
        "cogo.library-knowledge-bundle-provenance.v1" ||
      provenance.retrievalTraceId !== bundle.retrievalTraceId ||
      !isSafeIdentifier(provenance.bundleContentHash, 128)
    ) {
      return false
    }
    try {
      return (
        provenance.bundleContentHash ===
        (await stableJsonSha256({
          target: bundle.target,
          locale: bundle.locale,
          authorityContext: bundle.authorityContext,
          applicationBlueprints: bundle.applicationBlueprints,
          uxPatterns: bundle.uxPatterns,
          designOntologyReleases: bundle.designOntologyReleases,
          sourceInteractionRelationSets:
            bundle.sourceInteractionRelationSets,
        }))
      )
    } catch {
      return false
    }
  }
  if (!isProjectValidationProvenance(provenance)) return false
  try {
    if (!hasExactProjectProvenanceKeys(provenance)) return false
    if (
      bundle.applicationBlueprints.length !== 1 ||
      bundle.uxPatterns.length !== 1 ||
      bundle.designOntologyReleases.length !== 1
    ) return false
    const exactAssetRefs = [
      ...bundle.applicationBlueprints.map((asset) => `application-blueprint:${asset.summary.ref}`),
      ...bundle.uxPatterns.map((asset) => `ux-pattern:${asset.summary.ref}`),
      ...bundle.designOntologyReleases.map((asset) => `design-ontology-release:${asset.summary.ref}`),
    ].sort()
    const declaredAssetRefs = [...provenance.assetRefs].sort()
    const bundleContentHash = `sha256:${await stableJsonSha256({
      target: bundle.target,
      locale: bundle.locale,
      authorityContext: bundle.authorityContext,
      applicationBlueprints: bundle.applicationBlueprints,
      uxPatterns: bundle.uxPatterns,
      designOntologyReleases: bundle.designOntologyReleases,
      ...(bundle.sourceInteractionRelationSets !== undefined
        ? {
            sourceInteractionRelationSets:
              bundle.sourceInteractionRelationSets,
          }
        : {}),
    })}`
    const release = bundle.designOntologyReleases.find(
      (asset) => asset.summary.ref === authority.designCompositionReleaseRef,
    )
    const blueprintRefs = bundle.applicationBlueprints.map((asset) => asset.summary.ref)
    const uxPatternRefs = bundle.uxPatterns.map((asset) => asset.summary.ref)
    const { contentHash: _contentHash, ...body } = provenance
    return (
      provenance.retrievalTraceId === bundle.retrievalTraceId &&
      provenance.contractRef === authority.authorityContext.approvedProjectValidationContractRefs[0] &&
      provenance.contractHash === authority.authorityContext.contractHash &&
      provenance.aggregationScope === authority.authorityContext.aggregationScope &&
      provenance.aggregationScopeHash === hashProjectValidationAuthorityValue({
        aggregationScope: provenance.aggregationScope,
      }) &&
      provenance.sliceId === authority.authorityContext.sliceId &&
      provenance.sliceBindingHash === hashProjectValidationAuthorityValue({
        aggregationScope: provenance.aggregationScope,
        sliceId: provenance.sliceId,
        contractRef: provenance.contractRef,
        contractHash: provenance.contractHash,
      }) &&
      provenance.applicationCoreRef === authority.applicationCoreRef &&
      provenance.applicationCoreProjectionHash === hashProjectValidationAuthorityValue({
        applicationCoreRef: provenance.applicationCoreRef,
        applicationBlueprintRef: blueprintRefs[0],
        uxPatternRefs,
      }) &&
      provenance.designCompositionReleaseRef === authority.designCompositionReleaseRef &&
      provenance.designCompositionReleaseHash === authority.designCompositionReleaseHash &&
      provenance.sourceMaterializationReceiptRef === authority.sourceMaterializationReceiptRef &&
      provenance.sourceMaterializationReceiptHash === authority.sourceMaterializationReceiptHash &&
      provenance.authorityContextHash === authority.authorityContextHash &&
      hashProjectValidationAuthorityValue(bundle.authorityContext) === authority.authorityContextHash &&
      provenance.projectValidationAuthorityOptionHash === authority.contentHash &&
      provenance.nonPromotion === true &&
      declaredAssetRefs.length === exactAssetRefs.length &&
      declaredAssetRefs.every((ref, index) => ref === exactAssetRefs[index]) &&
      provenance.bundleContentHash === bundleContentHash &&
      release !== undefined &&
      hashProjectValidationAuthorityValue(release.payload) === authority.designCompositionReleaseHash &&
      provenance.contentHash === hashProjectValidationAuthorityValue(body)
    )
  } catch {
    return false
  }
}

const PROJECT_PROVENANCE_KEYS = [
  "schemaVersion", "provenanceKind", "retrievalTraceId", "contractRef",
  "contractHash", "aggregationScope", "aggregationScopeHash", "sliceId",
  "sliceBindingHash", "applicationCoreRef", "applicationCoreProjectionHash",
  "designCompositionReleaseRef", "designCompositionReleaseHash",
  "sourceMaterializationReceiptRef", "sourceMaterializationReceiptHash",
  "authorityContextHash", "projectValidationAuthorityOptionHash",
  "nonPromotion", "assetRefs", "bundleContentHash", "contentHash",
] as const

function isProjectValidationProvenance(
  value: unknown,
): value is ProjectValidationLibraryKnowledgeBundleProvenance {
  return isRecord(value) &&
    value.schemaVersion === "cogo.project-validation-library-knowledge-bundle-provenance.v1" &&
    value.provenanceKind === "project-validation"
}

function hasExactProjectProvenanceKeys(
  value: ProjectValidationLibraryKnowledgeBundleProvenance,
): boolean {
  const keys = Reflect.ownKeys(value)
  return keys.length === PROJECT_PROVENANCE_KEYS.length &&
    keys.every((key) => typeof key === "string" && PROJECT_PROVENANCE_KEYS.some((expected) => expected === key)) &&
    isSafeIdentifier(value.retrievalTraceId, 256) &&
    isSafeIdentifierArray(value.assetRefs, 256, 512) &&
    value.assetRefs.length === new Set(value.assetRefs).size &&
    [
      value.contractHash, value.aggregationScopeHash, value.sliceBindingHash,
      value.applicationCoreProjectionHash, value.designCompositionReleaseHash,
      value.sourceMaterializationReceiptHash, value.authorityContextHash,
      value.projectValidationAuthorityOptionHash, value.bundleContentHash,
      value.contentHash,
    ].every(isSha256)
}

function hasCandidates(
  bundle: unknown,
  request: LibraryRagOrchestrationRequest,
): bundle is LibraryKnowledgeBundle<LibraryKnowledgeBundleProvenanceEnvelope> {
  return (
    isRecord(bundle) &&
    bundle.schemaVersion === "cogo.library-knowledge-bundle.v1" &&
    isSafeIdentifier(bundle.retrievalTraceId, 256) &&
    bundle.target === request.target &&
    bundle.locale === request.locale &&
    isLibraryAuthorityContext(bundle.authorityContext) &&
    Array.isArray(bundle.applicationBlueprints) &&
    bundle.applicationBlueprints.length > 0 &&
    bundle.applicationBlueprints.every(isApplicationBlueprintAsset) &&
    hasUniqueCanonicalRefs(bundle.applicationBlueprints, applicationBlueprintRef) &&
    bundle.applicationBlueprints.every(
      (asset) =>
        asset.payload.target === request.target &&
        asset.payload.locale === request.locale,
    ) &&
    Array.isArray(bundle.uxPatterns) &&
    bundle.uxPatterns.length > 0 &&
    bundle.uxPatterns.every(isUxPatternAsset) &&
    hasUniqueCanonicalRefs(bundle.uxPatterns, uxPatternRef) &&
    bundle.uxPatterns.every((asset) => asset.payload.target === request.target) &&
    Array.isArray(bundle.designOntologyReleases) &&
    bundle.designOntologyReleases.length > 0 &&
    bundle.designOntologyReleases.every(isDesignOntologyReleaseAsset) &&
    hasUniqueCanonicalRefs(
      bundle.designOntologyReleases,
      (payload) => payload.releaseId,
    ) &&
    bundle.designOntologyReleases.every(
      (asset) => asset.payload.target === request.target,
    ) &&
    (bundle.sourceInteractionRelationSets === undefined ||
      (Array.isArray(bundle.sourceInteractionRelationSets) &&
        bundle.sourceInteractionRelationSets.every(
          isSourceInteractionRelationSetAsset,
        ) &&
        hasUniqueCanonicalRefs(
          bundle.sourceInteractionRelationSets,
          (payload) => payload.relationSetId,
        )))
  )
}

async function hasMatchingSourceInteractionRelationSets(
  bundle: LibraryKnowledgeBundle<LibraryKnowledgeBundleProvenanceEnvelope>,
  request: LibraryRagOrchestrationRequest,
): Promise<boolean> {
  const assets = bundle.sourceInteractionRelationSets
  if (assets === undefined || assets.length === 0) return true
  const admittedEvidenceRefs = new Set([
    ...bundle.authorityContext.admittedUxResearchRefs,
    ...bundle.authorityContext.approvedCanonicalSourceRefs,
    ...bundle.authorityContext.admittedApplicationCoreRefs,
  ])

  for (const asset of assets) {
    const payload = asset.payload
    const blueprint = assetFor(
      bundle.applicationBlueprints,
      payload.applicationBlueprintRef,
    )
    const release = assetFor(
      bundle.designOntologyReleases,
      payload.designOntologyReleaseRef,
    )
    if (
      !blueprint ||
      !release ||
      payload.applicationInstanceId !== request.applicationInstanceId ||
      !sameExactReferenceSet(
        payload.uxPatternRefs,
        blueprint.payload.uxInstances.map(
          (instance) => instance.definitionRef,
        ),
      ) ||
      !sameExactReferenceSet(
        payload.uxPatternRefs,
        bundle.uxPatterns.map((candidate) => candidate.summary.ref),
      ) ||
      !sameExactReferenceSet(
        payload.designSourceRefs,
        release.payload.sourceRefs,
      ) ||
      payload.evidenceRefs.length === 0 ||
      !payload.evidenceRefs.every((ref) => admittedEvidenceRefs.has(ref)) ||
      !sameExactReferenceSet(asset.summary.evidenceRefs, payload.evidenceRefs)
    ) {
      return false
    }
    const releaseComponents = new Set(
      release.payload.components.map((component) => component.componentId),
    )
    if (
      payload.relations.length === 0 ||
      payload.relations.some(
        (relation) => !releaseComponents.has(relation.componentRef),
      )
    ) {
      return false
    }
    const relationKeys = payload.relations.map(
      (relation) =>
        `${relation.blockId}\u0000${relation.sourceNodeId}\u0000${relation.elementId}\u0000${relation.componentRef}\u0000${relation.eventType}\u0000${relation.flowId}`,
    )
    if (new Set(relationKeys).size !== relationKeys.length) return false
    const { contentHash, ...body } = payload
    if (`sha256:${await stableJsonSha256(body)}` !== contentHash) return false
  }
  return true
}

export function projectLibraryKnowledgeSummaries(
  bundle: LibraryKnowledgeBundle<LibraryKnowledgeBundleProvenanceEnvelope>,
) {
  return {
    applicationBlueprints: bundle.applicationBlueprints.map((asset) => structuredClone(asset.summary)),
    uxPatterns: bundle.uxPatterns.map((asset) => structuredClone(asset.summary)),
    designOntologyReleases: bundle.designOntologyReleases.map((asset) => structuredClone(asset.summary)),
  }
}

/**
 * True only for the exact success object emitted by this process. Clones and
 * re-created records fail even when byte-identical. This is a local trusted
 * seam signal, never durable/offline provenance.
 */
export function isRuntimeAdmittedLibraryRagOrchestrationSuccess(
  value: unknown,
): value is Extract<LibraryRagOrchestrationResult, { ok: true }> {
  if (value === null || typeof value !== "object") return false
  const admittedHash = runtimeAdmittedSuccesses.get(value)
  if (admittedHash === undefined) return false
  try {
    return hashCanonicalContent(value) === admittedHash
  } catch {
    return false
  }
}

function resolveSelection(
  bundle: LibraryKnowledgeBundle<LibraryKnowledgeBundleProvenanceEnvelope>,
  selection: unknown,
  applicationInstanceId: string,
):
  | {
      ok: true
      selection: LibraryRagSelection
      applicationBlueprint: ApplicationBlueprint
      uxPatterns: UxPatternDefinition[]
      designOntologyRelease: LibraryKnowledgeBundle["designOntologyReleases"][number]["payload"]
      sourceInteractionRelations: SourceInteractionRelationSetV1["relations"]
      sourceInteractionRelationSet: SourceInteractionRelationSetV1 | null
    }
  | { ok: false; result: LibraryRagOrchestrationResult } {
  if (!isLibraryRagSelection(selection)) {
    return invalidSelection("The Library RAG selection is invalid.")
  }
  const blueprint = assetFor(bundle.applicationBlueprints, selection.applicationBlueprintRef)
  const release = assetFor(bundle.designOntologyReleases, selection.designOntologyReleaseRef)
  const uxPatterns = selection.uxPatternRefs.map((ref) => assetFor(bundle.uxPatterns, ref))
  if (!blueprint || !release || uxPatterns.some((pattern) => !pattern)) {
    return invalidSelection("A selected Library reference was not retrieved.")
  }
  const requiredRefs = [
    ...new Set(
      blueprint.payload.uxInstances.map((instance) => instance.definitionRef),
    ),
  ]
  if (!sameExactReferenceSet(selection.uxPatternRefs, requiredRefs)) {
    return invalidSelection("Selected UX Patterns do not exactly satisfy the Application Blueprint.")
  }
  const relationSetMatches = (bundle.sourceInteractionRelationSets ?? []).filter(
    (asset) =>
      asset.payload.applicationBlueprintRef ===
        selection.applicationBlueprintRef &&
      asset.payload.applicationInstanceId ===
        applicationInstanceId &&
      sameExactReferenceSet(
        asset.payload.uxPatternRefs,
        selection.uxPatternRefs,
      ) &&
      asset.payload.designOntologyReleaseRef ===
        selection.designOntologyReleaseRef,
  )
  if (
    (bundle.sourceInteractionRelationSets?.length ?? 0) > 0 &&
    relationSetMatches.length !== 1
  ) {
    return invalidSelection(
      "Selected Library references do not have exactly one matching source interaction relation set.",
    )
  }
  return {
    ok: true,
    selection,
    applicationBlueprint: blueprint.payload,
    uxPatterns: uxPatterns.map((pattern) => pattern!.payload),
    designOntologyRelease: release.payload,
    sourceInteractionRelations:
      relationSetMatches.length === 1
        ? structuredClone(relationSetMatches[0]!.payload.relations)
        : [],
    sourceInteractionRelationSet:
      relationSetMatches.length === 1
        ? structuredClone(relationSetMatches[0]!.payload)
        : null,
  }
}

function assetFor<Kind extends LibraryKnowledgeAsset<LibraryKnowledgeSummary["kind"], unknown>>(
  assets: Kind[],
  ref: string,
): Kind | undefined {
  return assets.find((asset) => asset.summary.ref === ref)
}

function sameExactReferenceSet(selected: string[], required: string[]): boolean {
  return (
    selected.length === required.length &&
    new Set(selected).size === selected.length &&
    [...selected].sort().every((ref, index) => ref === [...required].sort()[index])
  )
}

function invalidSelection(message: string): { ok: false; result: LibraryRagOrchestrationResult } {
  return { ok: false, result: failure("selection", "library-rag-selection-invalid", "$.selection", message) }
}

function isApplicationBlueprintAsset(
  asset: unknown,
): asset is LibraryKnowledgeBundle["applicationBlueprints"][number] {
  return isKnowledgeAsset(asset, "application-blueprint") && isApplicationBlueprint(asset.payload)
}

function isUxPatternAsset(
  asset: unknown,
): asset is LibraryKnowledgeBundle["uxPatterns"][number] {
  return isKnowledgeAsset(asset, "ux-pattern") && isUxPattern(asset.payload)
}

function isDesignOntologyReleaseAsset(
  asset: unknown,
): asset is LibraryKnowledgeBundle["designOntologyReleases"][number] {
  return isKnowledgeAsset(asset, "design-ontology-release") && isDesignOntologyRelease(asset.payload)
}

function isSourceInteractionRelationSetAsset(
  asset: unknown,
): asset is NonNullable<
  LibraryKnowledgeBundle["sourceInteractionRelationSets"]
>[number] {
  return (
    isKnowledgeAsset(asset, "source-interaction-relation-set") &&
    isSourceInteractionRelationSet(asset.payload)
  )
}

function isSourceInteractionRelationSet(
  value: unknown,
): value is SourceInteractionRelationSetV1 {
  return (
    hasExactKeys(value, [
      "schemaVersion",
      "relationSetId",
      "applicationBlueprintRef",
      "applicationInstanceId",
      "uxPatternRefs",
      "designOntologyReleaseRef",
      "designSourceRefs",
      "evidenceRefs",
      "relations",
      "contentHash",
    ]) &&
    value.schemaVersion === "cogo.source-interaction-relation-set.v1" &&
    isSafeIdentifier(value.relationSetId, 512) &&
    isSafeIdentifier(value.applicationBlueprintRef, 512) &&
    isSafeIdentifier(value.applicationInstanceId, 256) &&
    isSafeIdentifierArray(value.uxPatternRefs, 256, 512) &&
    value.uxPatternRefs.length > 0 &&
    isSafeIdentifier(value.designOntologyReleaseRef, 512) &&
    isSafeIdentifierArray(value.designSourceRefs, 256, 1_024) &&
    value.designSourceRefs.length > 0 &&
    isSafeIdentifierArray(value.evidenceRefs, 256, 1_024) &&
    Array.isArray(value.relations) &&
    value.relations.length > 0 &&
    value.relations.length <= 256 &&
    value.relations.every(isSourceInteractionRelation) &&
    isSha256(value.contentHash)
  )
}

function isSourceInteractionRelation(value: unknown): boolean {
  return (
    hasExactKeys(value, [
      "blockId",
      "sourceNodeId",
      "elementId",
      "componentRef",
      "eventType",
      "flowId",
    ]) &&
    [
      value.blockId,
      value.sourceNodeId,
      value.elementId,
      value.componentRef,
      value.eventType,
      value.flowId,
    ].every((ref) => isSafeText(ref, 2_048))
  )
}

function isKnowledgeAsset(
  asset: unknown,
  kind: LibraryKnowledgeSummary["kind"],
): asset is LibraryKnowledgeAsset<LibraryKnowledgeSummary["kind"], unknown> {
  return isRecord(asset) && isKnowledgeSummary(asset.summary, kind) && "payload" in asset
}

function isKnowledgeSummary(
  summary: unknown,
  kind: LibraryKnowledgeSummary["kind"],
): boolean {
  return (
    isRecord(summary) &&
    summary.kind === kind &&
    isSafeIdentifier(summary.ref, 512) &&
    isSafeText(summary.title, 512) &&
    isSafeText(summary.summary, 4_096) &&
    typeof summary.score === "number" &&
    Number.isFinite(summary.score) &&
    isSafeIdentifierArray(summary.evidenceRefs, 64, 512)
  )
}

/**
 * Bind the selected Blueprint's static capability declaration to this request.
 *
 * The request does not yet carry admitted requirement evidence, so the evidence
 * list is empty here. That is deliberately fail-closed: admission rejects every
 * enabled mode without evidence, so only an explicitly `disabled` Blueprint can
 * compile through this path until the orchestration request itself carries an
 * admitted `ApplicationCapabilityRequirement`.
 */
async function requirementForSelectedBlueprint(
  request: LibraryRagOrchestrationRequest,
  applicationBlueprint: ApplicationBlueprint,
): Promise<ApplicationCapabilityRequirement> {
  const digest = await stableJsonSha256(request)
  const requestHash: `sha256:${string}` = `sha256:${digest}`
  return {
    schemaVersion: "cogo.application-capability-requirement.v1",
    requestedWebMcpMode: applicationBlueprint.runtimeCapabilities.webMcp.mode,
    requestHash,
    requirementEvidenceRefs: [],
  }
}

function isApplicationBlueprint(value: unknown): value is ApplicationBlueprint {
  return (
    isRecord(value) &&
    value.schemaVersion === "cogo.application-blueprint.v1" &&
    isString(value.applicationBlueprintId) &&
    isString(value.version) &&
    isString(value.applicationCoreRef) &&
    value.target === "web" &&
    isString(value.locale) &&
    isString(value.recipeRef) &&
    // A retrieved Blueprint without an explicit capability declaration is not a
    // Blueprint this orchestrator may compile: reading its mode would throw.
    // Reject it here so the bundle fails closed with a typed gap instead.
    isRecord(value.runtimeCapabilities) &&
    isRecord((value.runtimeCapabilities as Record<string, unknown>).webMcp) &&
    isApplicationWebMcpMode(
      (
        (value.runtimeCapabilities as Record<string, unknown>).webMcp as Record<
          string,
          unknown
        >
      ).mode,
    ) &&
    Array.isArray(value.uxInstances) &&
    value.uxInstances.every(
      (instance) =>
        isRecord(instance) &&
        isString(instance.uxInstanceId) &&
        isString(instance.definitionRef),
    )
  )
}

function isUxPattern(value: unknown): value is UxPatternDefinition {
  return (
    isRecord(value) &&
    value.schemaVersion === "cogo.ux-pattern-definition.v1" &&
    isString(value.uxPatternId) &&
    isString(value.version) &&
    value.target === "web"
  )
}

function isDesignOntologyRelease(
  value: unknown,
): value is LibraryKnowledgeBundle["designOntologyReleases"][number]["payload"] {
  return (
    isRecord(value) &&
    value.schemaVersion === "cogo.design-composition-release.v1" &&
    isString(value.releaseId) &&
    value.target === "web"
  )
}

function isLibraryAuthorityContext(value: unknown): boolean {
  return (
    isRecord(value) &&
    isSafeIdentifierArray(value.admittedUxResearchRefs, 256, 1_024) &&
    isSafeIdentifierArray(value.approvedCanonicalSourceRefs, 256, 1_024) &&
    isSafeIdentifierArray(value.admittedApplicationCoreRefs, 256, 1_024)
  )
}

function isLibraryRagSelection(value: unknown): value is LibraryRagSelection {
  return (
    isRecord(value) &&
    value.schemaVersion === "cogo.library-rag-selection.v1" &&
    isSafeText(value.normalizedIntent, 4_096) &&
    isSafeIdentifier(value.applicationBlueprintRef, 512) &&
    isStringArray(value.uxPatternRefs) &&
    value.uxPatternRefs.length > 0 &&
    value.uxPatternRefs.length <= 256 &&
    value.uxPatternRefs.every((ref) => isSafeIdentifier(ref, 512)) &&
    isSafeIdentifier(value.designOntologyReleaseRef, 512) &&
    isStringArray(value.reasons) &&
    value.reasons.length <= 64 &&
    value.reasons.every((reason) => isSafeText(reason, 2_048)) &&
    isSafeIdentifier(value.traceId, 256)
  )
}

function isSemanticCriticVerdict(
  value: unknown,
): value is LibrarySemanticCriticVerdict {
  return (
    isRecord(value) &&
    value.schemaVersion === "cogo.library-semantic-critic-verdict.v1" &&
    (value.verdict === "pass" || value.verdict === "fail") &&
    Array.isArray(value.findings) &&
    value.findings.every(isSemanticCriticFinding) &&
    isSafeIdentifier(value.traceId, 256)
  )
}

function isSemanticCriticFinding(value: unknown): boolean {
  return (
    isRecord(value) &&
    (value.code === "semantic-mismatch" ||
      value.code === "ux-quality-gap" ||
      value.code === "knowledge-gap") &&
    isSafeText(value.message, 4_096) &&
    isSafeIdentifierArray(value.evidenceRefs, 64, 1_024)
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function hasExactKeys(
  value: unknown,
  expected: readonly string[],
): value is Record<string, unknown> {
  if (!isRecord(value)) return false
  const actual = Object.keys(value)
  return (
    actual.length === expected.length &&
    expected.every((key) => actual.includes(key))
  )
}

function isString(value: unknown): value is string {
  return typeof value === "string"
}

function isNonEmptyString(value: unknown): value is string {
  return isString(value) && value.trim().length > 0
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isString)
}

function isSha256(value: unknown): value is `sha256:${string}` {
  return typeof value === "string" && /^sha256:[a-f0-9]{64}$/.test(value)
}

function admittedEvidenceRefsFor(
  artifact: Extract<LibraryRagOrchestrationResult, { ok: true }> ["artifact"],
): Set<string> {
  return new Set([
    artifact.library.applicationBlueprintRef,
    ...artifact.library.provenance.uxPatternRefs,
    ...artifact.library.provenance.sourceRefs,
    artifact.designOntologyReleaseRef,
  ])
}

function criticEvidenceRefsAreAdmitted(
  findings: LibrarySemanticCriticVerdict["findings"],
  allowedEvidenceRefs: Set<string>,
): boolean {
  return findings.every((finding) =>
    finding.evidenceRefs.every((ref) => allowedEvidenceRefs.has(ref)),
  )
}

function exactEvidenceRefs(
  findings: LibrarySemanticCriticVerdict["findings"],
): string[] {
  return [
    ...new Set(
      findings.flatMap((finding) => finding.evidenceRefs),
    ),
  ]
}

function hasUniqueCanonicalRefs<Payload>(
  assets: Array<LibraryKnowledgeAsset<LibraryKnowledgeSummary["kind"], Payload>>,
  canonicalRef: (payload: Payload) => string,
): boolean {
  const refs = assets.map((asset) => asset.summary.ref)
  return (
    new Set(refs).size === refs.length &&
    assets.every((asset) => asset.summary.ref === canonicalRef(asset.payload))
  )
}

function applicationBlueprintRef(payload: ApplicationBlueprint): string {
  return `${payload.applicationBlueprintId}@${payload.version}`
}

function uxPatternRef(payload: UxPatternDefinition): string {
  return `${payload.uxPatternId}@${payload.version}`
}

function isAgentIdentity<Role extends "knowledge-planner" | "semantic-critic">(
  value: unknown,
  role: Role,
): value is LibraryAgentIdentity<Role> {
  return (
    isRecord(value) &&
    value.role === role &&
    isSafeIdentifier(value.provider, 128) &&
    isSafeIdentifier(value.model, 256)
  )
}

const UNSAFE_RECEIPT_PATTERN =
  /(?:authorization\s*:|bearer\s+[a-z0-9._~+/-]+|(?:api[_-]?key|client[_-]?secret|access[_-]?token|password)\s*[:=]|(?:https?|wss?|ftp):\/\/|\bsk-[a-z0-9_-]{12,}|\beyj[a-z0-9_-]{8,}\.[a-z0-9_-]{8,}\.[a-z0-9_-]{8,})/i

function isSafeText(value: unknown, maxLength: number): value is string {
  return (
    isNonEmptyString(value) &&
    value.length <= maxLength &&
    !UNSAFE_RECEIPT_PATTERN.test(value)
  )
}

function isSafeIdentifier(value: unknown, maxLength: number): value is string {
  return (
    isSafeText(value, maxLength) &&
    /^[A-Za-z0-9][A-Za-z0-9._:/@+-]*$/.test(value) &&
    !value.includes("://")
  )
}

function isSafeIdentifierArray(
  value: unknown,
  maxItems: number,
  maxLength: number,
): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= maxItems &&
    value.every((item) => isSafeIdentifier(item, maxLength))
  )
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value)
    for (const child of Object.values(value as Record<string, unknown>)) {
      deepFreeze(child)
    }
  }
  return value
}

function failure(
  stage: Extract<LibraryRagOrchestrationResult, { ok: false }> ["stage"],
  code: LibraryRagOrchestrationGap["code"],
  path: string,
  message: string,
  evidenceRefs: string[] = [],
): LibraryRagOrchestrationResult {
  return { ok: false, stage, gaps: [{ code, path, message, evidenceRefs }] }
}
